import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { theme } from '../../theme';
import { CATEGORIES, PRICE_UNITS } from '../../constants';
import { useApi } from '../../hooks/useApi';

export function ServiceCreateScreen({ onNavigate }) {
  const { isAuthenticated } = useAuth();
  const api = useApi();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    category: '',
    title: '',
    description: '',
    base_price: '',
    price_unit: 'per_item',
    turnaround_days: '',
    materials: '',
  });

  if (!isAuthenticated) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', marginTop: '40px' }}>
        <p style={{ fontSize: '40px', marginBottom: '16px' }}>🔒</p>
        <p style={{ color: theme.text, fontWeight: 600 }}>Skráðu þig inn</p>
      </div>
    );
  }

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    if (!form.category || !form.title.trim()) {
      setError('Flokkur og titill eru nauðsynleg');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.services.create({
        category: form.category,
        title: form.title.trim(),
        description: form.description.trim() || null,
        base_price: form.base_price ? parseFloat(form.base_price) : null,
        price_unit: form.price_unit,
        turnaround_days: form.turnaround_days ? parseInt(form.turnaround_days) : null,
        materials: form.materials ? form.materials.split(',').map(m => m.trim()) : null,
      });
      onNavigate('my-page');
    } catch (err) {
      setError(err.message || 'Villa kom upp');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <button
        onClick={() => onNavigate('my-page')}
        style={{ background: 'none', border: 'none', color: theme.accent, cursor: 'pointer', marginBottom: '16px' }}
      >
        ← Til baka
      </button>

      <h2 style={{ fontSize: '18px', fontWeight: 700, color: theme.text, marginBottom: '4px' }}>
        Ný þjónusta
      </h2>
      <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '20px' }}>
        Bættu við þjónustu sem þú býður upp á
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Category */}
        <div>
          <label style={labelStyle}>Flokkur *</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => update('category', cat.id)}
                style={{
                  background: form.category === cat.id ? theme.accentBg : theme.bgCard,
                  border: `1px solid ${form.category === cat.id ? theme.accent : theme.border}`,
                  borderRadius: theme.radiusSm,
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: '18px' }}>{cat.icon}</span>
                <span style={{ fontSize: '13px', color: theme.text, fontWeight: 500 }}>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div>
          <label style={labelStyle}>Heiti þjónustu *</label>
          <input
            value={form.title}
            onChange={e => update('title', e.target.value)}
            placeholder="T.d. PLA 3D prentun, Laser leturgröftur..."
            style={inputStyle}
          />
        </div>

        {/* Description */}
        <div>
          <label style={labelStyle}>Lýsing (valfrjálst)</label>
          <textarea
            value={form.description}
            onChange={e => update('description', e.target.value)}
            placeholder="Lýstu þjónustunni, stærðartakmörkunum, gæðum..."
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {/* Price + Unit */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Grunnverð (kr.)</label>
            <input
              type="number"
              min="0"
              value={form.base_price}
              onChange={e => update('base_price', e.target.value)}
              placeholder="T.d. 2000"
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Verðeining</label>
            <select
              value={form.price_unit}
              onChange={e => update('price_unit', e.target.value)}
              style={inputStyle}
            >
              {Object.entries(PRICE_UNITS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Turnaround + Materials */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Afhendingartími (dagar)</label>
            <input
              type="number"
              min="1"
              value={form.turnaround_days}
              onChange={e => update('turnaround_days', e.target.value)}
              placeholder="T.d. 3"
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Efni (kommu-aðskilin)</label>
            <input
              value={form.materials}
              onChange={e => update('materials', e.target.value)}
              placeholder="PLA, PETG, ABS"
              style={inputStyle}
            />
          </div>
        </div>

        {error && (
          <div style={{
            background: `${theme.error}15`,
            border: `1px solid ${theme.error}`,
            borderRadius: theme.radiusSm,
            padding: '10px 14px',
            color: theme.error,
            fontSize: '13px',
          }}>
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={submitting || !form.category || !form.title.trim()}
          style={{
            padding: '14px',
            background: theme.accent,
            color: '#000',
            border: 'none',
            borderRadius: theme.radiusSm,
            fontWeight: 700,
            fontSize: '15px',
            cursor: submitting ? 'wait' : 'pointer',
            opacity: submitting || !form.category || !form.title.trim() ? 0.6 : 1,
          }}
        >
          {submitting ? 'Vista...' : 'Vista þjónustu'}
        </button>
      </div>
    </div>
  );
}

const labelStyle = {
  fontSize: '13px',
  fontWeight: 600,
  color: theme.textSecondary,
  display: 'block',
  marginBottom: '6px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: theme.bgInput,
  border: `1px solid ${theme.border}`,
  borderRadius: theme.radiusSm,
  color: theme.text,
  fontSize: '14px',
  outline: 'none',
};
